/**
 * Shared Scene Constants
 *
 * Single source of truth for material and lighting defaults.
 * Used by both game client and pallet viewer for consistent rendering.
 */
// ============== Material Shader Defaults ==============
/** Default roughness multiplier - applied to roughness texture values */
export const MATERIAL_ROUGHNESS_MULTIPLIER = 0.9;
/** Default metalness offset - added to metalness texture values */
export const MATERIAL_METALNESS_OFFSET = 0.05;
/** Default AO intensity - 0 = no AO effect, 1 = full effect */
export const MATERIAL_AO_INTENSITY = 1.0;
/**
 * Foliage (transparent layer) lighting — see TerrainMaterial's FOLIAGE_LIGHTING. The canopy is a
 * blob of thin leaves, so direct light wraps further round it than Lambert allows: the shader lights
 * it with `saturate((N·L + w) / (1 + w))` (w = this value; 0 = plain Lambert), unshadowed, so top >
 * sun-side > far side > underside without the underside going black — and biases the hemisphere
 * weight toward the SKY colour for down-facing normals (a leaf's underside sees sky through the
 * canopy, not the ground). Both are tunable in the debug panel's Materials section.
 */
export const FOLIAGE_TRANSLUCENCY = 0.5;
/** 0 = plain hemisphere (undersides see the ground colour), 1 = undersides weighted like tops. */
export const FOLIAGE_SKY_BIAS = 1.0;
/** Default normal map strength - multiplied with normal map values */
export const MATERIAL_NORMAL_STRENGTH = 1.0;
// ============== Lighting Defaults ==============
/** Sun light color - warm sunset yellow */
export const LIGHT_SUN_COLOR = '#ffcc00';
/** Main directional light (sun) intensity */
export const LIGHT_SUN_INTENSITY = 4.0;
// ============== Environment/IBL Defaults ==============
/** Environment map intensity for IBL reflections */
export const ENVIRONMENT_INTENSITY = 1.0;
/** Default skybox preset */
export const DEFAULT_SKYBOX = 'forest';
// ============== Placeholder Texture Defaults ==============
// Values for generated textures when no source texture exists
/** Default AO value (255 = no occlusion, white) */
export const PLACEHOLDER_AO = 255;
/** Default roughness value (200/255 ≈ 0.78 = fairly rough) */
export const PLACEHOLDER_ROUGHNESS = 200;
/** Default metalness value (0 = non-metallic) */
export const PLACEHOLDER_METALNESS = 0;
/** Default normal map color (flat normal pointing up: RGB 128, 128, 255) */
export const PLACEHOLDER_NORMAL = { r: 128, g: 128, b: 255 };
// ============== Day-Night Cycle Defaults ==============
/** Default time of day (0-1, 0.375 = 9:00am morning light) */
export const DEFAULT_TIME_OF_DAY = 0.375;
/**
 * Default time speed in game-minutes per real-second.
 * 1.2 = one full day (1440 game-min) every 20 real minutes (1200 s). 0 = paused.
 */
export const DEFAULT_TIME_SPEED = 1.2;
/** Sun orbital elevation range (degrees) */
export const SUN_ELEVATION_MIN = -20; // Below horizon at midnight
export const SUN_ELEVATION_MAX = 70; // Max altitude at noon
/** Sun distance from origin for light positioning */
export const SUN_DISTANCE = 150;
/** Moon light color - soft cool blue */
export const LIGHT_MOON_COLOR = '#aabbdd';
/** Moon intensity at peak - subtle directional, not main light source */
export const LIGHT_MOON_INTENSITY = 0.8;
/** Sun color at different times (interpolated) */
export const SUN_COLOR_NOON = '#fffaf0'; // Warm white at noon
export const SUN_COLOR_GOLDEN = '#ffcc44'; // Golden hour
export const SUN_COLOR_SUNSET = '#ff6633'; // Sunset/sunrise red
export const SUN_COLOR_TWILIGHT = '#334466'; // Twilight blue
/** Sunrise-specific colors (pink/blue morning) */
export const SUN_COLOR_SUNRISE_PINK = '#ffaacc'; // Soft pink dawn
export const SUN_COLOR_SUNRISE_PEACH = '#ffbb88'; // Peach morning
/** Sunset-specific colors (deep orange/purple evening) */
export const SUN_COLOR_SUNSET_DEEP = '#ff4411'; // Deep orange sunset
export const SUN_COLOR_SUNSET_PURPLE = '#993366'; // Purple dusk
/** Hemisphere light - sky color at different times */
export const HEMISPHERE_SKY_DAY = '#87ceeb'; // Light sky blue
export const HEMISPHERE_SKY_SUNRISE = '#ffccdd'; // Light pink morning
export const HEMISPHERE_SKY_SUNRISE_BLUE = '#aaddff'; // Soft blue morning
export const HEMISPHERE_SKY_SUNSET = '#ff8844'; // Deep orange sunset
export const HEMISPHERE_SKY_SUNSET_PURPLE = '#8877aa'; // Purple dusk (brightness matched to night)
export const HEMISPHERE_SKY_NIGHT = '#7799cc'; // Bright moonlit sky (main night fill)
/** Hemisphere light - ground color at different times */
export const HEMISPHERE_GROUND_DAY = '#4a6a4a'; // Green earth
export const HEMISPHERE_GROUND_SUNRISE = '#887766'; // Warm brown morning
export const HEMISPHERE_GROUND_SUNSET = '#553322'; // Deep warm brown
export const HEMISPHERE_GROUND_NIGHT = '#556688'; // Blue-gray ground (bright enough to see)
/** Hemisphere light intensity range (primary fill light) */
export const HEMISPHERE_INTENSITY_DAY = 0.8;
export const HEMISPHERE_INTENSITY_NIGHT = 1.5; // Main light source at night
/** Environment/IBL intensity range (lower at night since we don't change envmap) */
export const ENVIRONMENT_INTENSITY_DAY = 1.0;
export const ENVIRONMENT_INTENSITY_NIGHT = 0.15;
/** Time thresholds (0-1 normalized) for day phases - longer sunrise/sunset */
export const TIME_SUNRISE_START = 0.15; // 3:36 AM - early dawn
export const TIME_SUNRISE_END = 0.35; // 8:24 AM - mid morning
export const TIME_SUNSET_START = 0.65; // 3:36 PM - afternoon
export const TIME_SUNSET_END = 0.85; // 8:24 PM - late dusk
/** Moon visibility thresholds based on sun elevation (degrees) */
export const MOON_THRESHOLD_INVISIBLE = 45; // Sun above this = no moon
export const MOON_THRESHOLD_FAINT = 25; // Moon starts appearing
export const MOON_THRESHOLD_RISING = 10; // Moon ramping up
export const MOON_THRESHOLD_FULL = -5; // Moon at full intensity
/** Color gradient stops for sun during sunrise [t, color][] */
export const SUN_GRADIENT_SUNRISE = [
    [0.0, SUN_COLOR_TWILIGHT],
    [0.2, SUN_COLOR_SUNRISE_PINK],
    [0.4, SUN_COLOR_SUNRISE_PEACH],
    [0.7, SUN_COLOR_GOLDEN],
    [1.0, SUN_COLOR_NOON],
];
/** Color gradient stops for sun during sunset [t, color][] */
export const SUN_GRADIENT_SUNSET = [
    [0.0, SUN_COLOR_NOON],
    [0.25, SUN_COLOR_GOLDEN],
    [0.5, SUN_COLOR_SUNSET_DEEP],
    [0.75, SUN_COLOR_SUNSET_PURPLE],
    [1.0, SUN_COLOR_TWILIGHT],
];
/** Color gradient stops for hemisphere sky during sunrise */
export const HEMISPHERE_SKY_GRADIENT_SUNRISE = [
    [0.0, HEMISPHERE_SKY_NIGHT],
    [0.3, HEMISPHERE_SKY_SUNRISE],
    [0.6, HEMISPHERE_SKY_SUNRISE_BLUE],
    [1.0, HEMISPHERE_SKY_DAY],
];
/** Color gradient stops for hemisphere sky during sunset */
export const HEMISPHERE_SKY_GRADIENT_SUNSET = [
    [0.0, HEMISPHERE_SKY_DAY],
    [0.3, HEMISPHERE_SKY_SUNSET],
    [0.6, HEMISPHERE_SKY_SUNSET_PURPLE],
    [1.0, HEMISPHERE_SKY_NIGHT],
];
//# sourceMappingURL=constants.js.map